import React, { useRef, useState, useEffect, useCallback } from 'react';
import { sendPlaylistEvent } from '../utils/playlistEvents';
import VideoBlock from './blocks/VideoBlock';
import ImageBlock from './blocks/ImageBlock';
import TextBlock from './blocks/TextBlock';
import AudioBlock from './blocks/AudioBlock';
import QuizBlock from './blocks/QuizBlock';
import CTABlock from './blocks/CTABlock';

export default function CourseReelPlayer({ playlist, blocks, currentIndex, onNext, onPrev, onSeek }) {
  const [watchStartTime, setWatchStartTime] = useState(null);
  const containerRef = useRef(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const startY = useRef(0);
  const startTime = useRef(0);
  const impressionSent = useRef({});

  const currentBlock = blocks[currentIndex];
  const persistentCTA = getPersistentCTA(blocks);

  // Send playlist_open on mount
  useEffect(() => {
    if (playlist) {
      sendPlaylistEvent(playlist.id, 'playlist_open', {
        total_blocks: blocks.length,
        resume_index: currentIndex
      });
      setWatchStartTime(Date.now());
    }
  }, [playlist?.id]);

  // Send playlist_resume when index changes (not first block)
  useEffect(() => {
    if (currentIndex > 0 && playlist) {
      sendPlaylistEvent(playlist.id, 'playlist_resume', {
        block_index: currentIndex,
        watch_ms: watchStartTime ? Date.now() - watchStartTime : 0
      });
    }
  }, [currentIndex]);

  // Send block_impression for current block
  useEffect(() => {
    if (!currentBlock || impressionSent.current[currentIndex]) return;

    const timer = setTimeout(() => {
      sendPlaylistEvent(playlist.id, 'block_impression', {
        block_id: currentBlock.id,
        block_index: currentIndex,
        block_type: currentBlock.type
      });
      impressionSent.current[currentIndex] = true;
    }, 1000);

    return () => clearTimeout(timer);
  }, [currentIndex, currentBlock, playlist?.id]);

  // Send playlist_complete when reaching last block
  useEffect(() => {
    if (currentIndex === blocks.length - 1 && playlist) {
      sendPlaylistEvent(playlist.id, 'playlist_complete', {
        total_blocks: blocks.length,
        watch_ms: watchStartTime ? Date.now() - watchStartTime : 0
      });
    }
  }, [currentIndex, blocks.length, playlist?.id, watchStartTime]);

  // Velocity-based swipe handling
  const handleTouchStart = useCallback((e) => {
    startY.current = e.touches[0].clientY;
    startTime.current = Date.now();
    setIsSwiping(true);
    setSwipeOffset(0);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isSwiping) return;
    
    const currentY = e.touches[0].clientY;
    const diff = startY.current - currentY;
    setSwipeOffset(diff);

    if (Math.abs(diff) > 10) {
      e.preventDefault();
    }
  }, [isSwiping]);

  const handleTouchEnd = useCallback((e) => {
    if (!isSwiping) return;

    const endY = e.changedTouches[0].clientY;
    const diff = startY.current - endY;
    const duration = Date.now() - startTime.current;
    const velocity = Math.abs(diff) / duration;
    const threshold = velocity > 0.5 ? 30 : 80;
    
    if (diff > threshold) {
      onNext();
    } else if (diff < -threshold) {
      onPrev();
    }

    setIsSwiping(false);
    setSwipeOffset(0);
  }, [isSwiping, onNext, onPrev]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        onNext();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNext, onPrev]);

  // Mouse wheel handling for desktop
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    
    const now = Date.now();
    if (now - (window.lastWheelTime || 0) < 500) return;
    window.lastWheelTime = now;
    
    if (e.deltaY > 0) {
      onNext();
    } else if (e.deltaY < 0) {
      onPrev();
    }
  }, [onNext, onPrev]);

  if (!currentBlock) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-black text-white">
        No block to display
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full w-full relative"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
    >
      {/* Render current block based on type */}
      {currentBlock.type === 'video' && (
        <VideoBlock
          block={currentBlock}
          playlist={playlist}
          blockIndex={currentIndex}
          isActive={true}
          onComplete={onNext}
          persistentCTA={persistentCTA}
        />
      )}
      
      {currentBlock.type === 'image' && (
        <ImageBlock
          block={currentBlock}
          playlist={playlist}
          blockIndex={currentIndex}
          onComplete={onNext}
          persistentCTA={persistentCTA}
        />
      )}
      
      {currentBlock.type === 'text' && (
        <TextBlock
          block={currentBlock}
          playlist={playlist}
          blockIndex={currentIndex}
          onComplete={onNext}
          persistentCTA={persistentCTA}
        />
      )}
      
      {currentBlock.type === 'audio' && (
        <AudioBlock
          block={currentBlock}
          playlist={playlist}
          blockIndex={currentIndex}
          onComplete={onNext}
          persistentCTA={persistentCTA}
        />
      )}
      
      {currentBlock.type === 'quiz' && (
        <QuizBlock
          block={currentBlock}
          playlist={playlist}
          blockIndex={currentIndex}
          onComplete={onNext}
          persistentCTA={persistentCTA}
        />
      )}
      
      {currentBlock.type === 'cta' && (
        <CTABlock
          block={currentBlock}
          playlist={playlist}
          blockIndex={currentIndex}
          onComplete={onNext}
        />
      )}

      {/* Progress bar - top */}
      <div 
        className="absolute left-0 right-0 h-1 bg-white/30" 
        style={{ 
          top: 'env(safe-area-inset-top, 0px)',
          zIndex: 40
        }}
      >
        <div
          className="h-full bg-white transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / blocks.length) * 100}%` }}
        />
      </div>
    </div>
  );
}

// Helper: Get last CTA from blocks (for persistent CTA)
function getPersistentCTA(blocks) {
  const ctaBlocks = blocks.filter(b => b.type === 'cta');
  if (ctaBlocks.length === 0) return null;
  
  // Get last CTA
  const lastCTA = ctaBlocks[ctaBlocks.length - 1];
  return {
    label: lastCTA.props?.button_text || 'Learn More',
    url: lastCTA.props?.button_url || '#'
  };
}
